/**
 * TimeSeriesChart Component
 *
 * Displays time series data as an area/line chart with optional features:
 * - Multiple data series
 * - Configurable colors
 * - Time axis formatting
 * - Hover tooltips
 * - Legend
 */

import type { ReactElement, MouseEvent } from "react";
import { useState, useCallback, useMemo } from "react";

/** Data point for time series */
export interface TimeSeriesDataPoint {
  readonly timestamp: string;
  readonly value: number;
  readonly label?: string;
}

/** Data series configuration */
export interface TimeSeriesSeries {
  readonly id: string;
  readonly name: string;
  readonly data: readonly TimeSeriesDataPoint[];
  readonly color: ChartColor;
}

/** Chart color options */
export type ChartColor = "primary" | "success" | "warning" | "error" | "info" | "purple" | "teal";

/** Chart props */
export interface TimeSeriesChartProps {
  /** Single data series */
  readonly data?: readonly TimeSeriesDataPoint[];
  /** Multiple data series */
  readonly series?: readonly TimeSeriesSeries[];
  /** Chart color (for single series) */
  readonly color?: ChartColor;
  /** Chart title */
  readonly title?: string;
  /** Y-axis label */
  readonly yAxisLabel?: string;
  /** Height in pixels */
  readonly height?: number;
  /** Show legend */
  readonly showLegend?: boolean;
  /** Show grid lines */
  readonly showGrid?: boolean;
  /** Show axis labels */
  readonly showAxisLabels?: boolean;
  /** Format value for display */
  readonly formatValue?: (value: number) => string;
  /** Format timestamp for axis */
  readonly formatTime?: (timestamp: string) => string;
  /** Empty state message */
  readonly emptyMessage?: string;
  /** Loading state */
  readonly isLoading?: boolean;
  /** Additional CSS classes */
  readonly className?: string;
}

/** Color configuration */
const COLOR_CONFIG: Record<ChartColor, { stroke: string; fill: string }> = {
  primary: { stroke: "stroke-primary", fill: "fill-primary/20" },
  success: { stroke: "stroke-success", fill: "fill-success/20" },
  warning: { stroke: "stroke-warning", fill: "fill-warning/20" },
  error: { stroke: "stroke-error", fill: "fill-error/20" },
  info: { stroke: "stroke-sky-500", fill: "fill-sky-500/20" },
  purple: { stroke: "stroke-purple-500", fill: "fill-purple-500/20" },
  teal: { stroke: "stroke-teal-500", fill: "fill-teal-500/20" },
};

const COLOR_HEX: Record<ChartColor, string> = {
  primary: "#3b82f6",
  success: "#22c55e",
  warning: "#eab308",
  error: "#ef4444",
  info: "#0ea5e9",
  purple: "#a855f7",
  teal: "#14b8a6",
};

/** Default time formatter */
function defaultFormatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Default value formatter */
function defaultFormatValue(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  if (value % 1 !== 0) {
    return value.toFixed(2);
  }
  return value.toString();
}

/**
 * Tooltip component for hovering over data points.
 */
interface TooltipProps {
  readonly x: number;
  readonly y: number;
  readonly timestamp: string;
  readonly values: readonly { name: string; value: number; color: ChartColor }[];
  readonly formatValue: (value: number) => string;
  readonly formatTime: (timestamp: string) => string;
}

function Tooltip({ x, y, timestamp, values, formatValue, formatTime }: TooltipProps): ReactElement {
  return (
    <div
      className="absolute z-10 pointer-events-none transform -translate-x-1/2 -translate-y-full"
      style={{ left: x, top: y - 10 }}
    >
      <div className="bg-background-secondary border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
        <p className="text-foreground-muted mb-1">{formatTime(timestamp)}</p>
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: COLOR_HEX[v.color] }}
            />
            <span className="text-foreground-muted">{v.name}:</span>
            <span className="font-medium">{formatValue(v.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Loading skeleton for chart.
 */
function ChartSkeleton({ height }: { readonly height: number }): ReactElement {
  return (
    <div className="animate-pulse" style={{ height }}>
      <div className="h-full bg-foreground-muted/10 rounded-lg" />
    </div>
  );
}

/**
 * Empty state for chart.
 */
function ChartEmpty({
  message,
  height,
}: {
  readonly message: string;
  readonly height: number;
}): ReactElement {
  return (
    <div
      className="flex items-center justify-center text-foreground-muted text-sm"
      style={{ height }}
    >
      {message}
    </div>
  );
}

/**
 * TimeSeriesChart component for displaying time series data.
 */
export function TimeSeriesChart({
  data,
  series,
  color = "primary",
  title,
  yAxisLabel,
  height = 200,
  showLegend = false,
  showGrid = true,
  showAxisLabels = true,
  formatValue = defaultFormatValue,
  formatTime = defaultFormatTime,
  emptyMessage = "No data available",
  isLoading = false,
  className = "",
}: TimeSeriesChartProps): ReactElement {
  const [hoverInfo, setHoverInfo] = useState<{
    x: number;
    y: number;
    index: number;
  } | null>(null);

  // Normalize to series format
  const allSeries = useMemo((): readonly TimeSeriesSeries[] => {
    if (series) return series;
    if (data && data.length > 0) {
      return [{ id: "default", name: "Value", data, color }];
    }
    return [];
  }, [data, series, color]);

  // Calculate chart bounds
  const chartInfo = useMemo(() => {
    const firstSeries = allSeries[0];
    if (!firstSeries || firstSeries.data.length === 0) {
      return null;
    }

    const allValues = allSeries.flatMap((s) => s.data.map((d) => d.value));
    const max = Math.max(...allValues);
    const min = Math.min(...allValues);
    const range = max - min || 1;
    const dataLength = firstSeries.data.length;

    return { min, max, range, dataLength, firstSeries };
  }, [allSeries]);

  // Handle mouse move for tooltip
  const handleMouseMove = useCallback(
    (e: MouseEvent<Element>) => {
      if (!chartInfo) return;

      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const chartWidth = rect.width - (showAxisLabels ? 40 : 0);
      const offsetX = showAxisLabels ? 40 : 0;
      const relativeX = x - offsetX;

      if (relativeX < 0 || relativeX > chartWidth) {
        setHoverInfo(null);
        return;
      }

      const index = Math.round((relativeX / chartWidth) * (chartInfo.dataLength - 1));
      const clampedIndex = Math.max(0, Math.min(index, chartInfo.dataLength - 1));

      setHoverInfo({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        index: clampedIndex,
      });
    },
    [chartInfo, showAxisLabels]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  if (isLoading) {
    return <ChartSkeleton height={height} />;
  }

  if (!chartInfo || allSeries.length === 0) {
    return <ChartEmpty message={emptyMessage} height={height} />;
  }

  // SVG dimensions
  const svgWidth = 100;
  const svgHeight = height;
  const chartPadding = {
    top: 10,
    right: 10,
    bottom: showAxisLabels ? 30 : 10,
    left: showAxisLabels ? 40 : 10,
  };
  const chartWidth = svgWidth - chartPadding.left - chartPadding.right;
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;

  // Generate paths for each series
  const seriesPaths = allSeries.map((s) => {
    const points = s.data.map((d, i) => {
      const x = chartPadding.left + (i / (chartInfo.dataLength - 1)) * chartWidth;
      const y =
        chartPadding.top +
        chartHeight -
        ((d.value - chartInfo.min) / chartInfo.range) * chartHeight;
      return { x, y };
    });

    const linePath = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
      .join(" ");

    const areaPath = `${linePath} L${(chartPadding.left + chartWidth).toFixed(2)},${(chartPadding.top + chartHeight).toFixed(2)} L${chartPadding.left},${(chartPadding.top + chartHeight).toFixed(2)} Z`;

    return { id: s.id, color: s.color, linePath, areaPath };
  });

  // Grid lines
  const gridLines = showGrid
    ? [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
        y: chartPadding.top + chartHeight * (1 - ratio),
        value: chartInfo.min + chartInfo.range * ratio,
      }))
    : [];

  // Time axis labels
  const firstData = chartInfo.firstSeries.data[0];
  const lastData = chartInfo.firstSeries.data[chartInfo.dataLength - 1];

  return (
    <div className={`relative ${className}`}>
      {title && <h4 className="text-sm font-medium text-foreground-muted mb-2">{title}</h4>}

      <div className="relative" style={{ height }}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full h-full"
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Grid lines */}
          {showGrid &&
            gridLines.map((line, i) => (
              <line
                key={i}
                x1={chartPadding.left}
                y1={line.y}
                x2={chartPadding.left + chartWidth}
                y2={line.y}
                className="stroke-border"
                strokeWidth={0.5}
                strokeDasharray="2,2"
              />
            ))}

          {/* Series areas and lines */}
          {seriesPaths.map((s) => (
            <g key={s.id}>
              <path d={s.areaPath} className={COLOR_CONFIG[s.color].fill} />
              <path
                d={s.linePath}
                className={`${COLOR_CONFIG[s.color].stroke} fill-none`}
                strokeWidth={2}
              />
            </g>
          ))}

          {/* Hover line */}
          {hoverInfo && (
            <line
              x1={chartPadding.left + (hoverInfo.index / (chartInfo.dataLength - 1)) * chartWidth}
              y1={chartPadding.top}
              x2={chartPadding.left + (hoverInfo.index / (chartInfo.dataLength - 1)) * chartWidth}
              y2={chartPadding.top + chartHeight}
              className="stroke-foreground-muted/50"
              strokeWidth={1}
              strokeDasharray="4,4"
            />
          )}
        </svg>

        {/* Y-axis labels */}
        {showAxisLabels && (
          <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-foreground-muted py-2">
            <span>{formatValue(chartInfo.max)}</span>
            <span>{formatValue(chartInfo.min)}</span>
          </div>
        )}

        {/* X-axis labels */}
        {showAxisLabels && firstData && lastData && (
          <div className="absolute bottom-0 left-10 right-0 flex justify-between text-xs text-foreground-muted">
            <span>{formatTime(firstData.timestamp)}</span>
            <span>{formatTime(lastData.timestamp)}</span>
          </div>
        )}

        {/* Y-axis label */}
        {yAxisLabel && showAxisLabels && (
          <div className="absolute -left-4 top-1/2 -translate-y-1/2 -rotate-90 text-xs text-foreground-muted whitespace-nowrap">
            {yAxisLabel}
          </div>
        )}

        {/* Tooltip */}
        {(() => {
          if (!hoverInfo) return null;
          const hoverDataPoint = chartInfo.firstSeries.data[hoverInfo.index];
          if (!hoverDataPoint) return null;
          return (
            <Tooltip
              x={hoverInfo.x}
              y={hoverInfo.y}
              timestamp={hoverDataPoint.timestamp}
              values={allSeries.map((s) => {
                const dataPoint = s.data[hoverInfo.index];
                return {
                  name: s.name,
                  value: dataPoint?.value ?? 0,
                  color: s.color,
                };
              })}
              formatValue={formatValue}
              formatTime={formatTime}
            />
          );
        })()}
      </div>

      {/* Legend */}
      {showLegend && allSeries.length > 1 && (
        <div className="flex items-center gap-4 mt-2 text-xs">
          {allSeries.map((s) => (
            <div key={s.id} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: COLOR_HEX[s.color] }}
              />
              <span className="text-foreground-muted">{s.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TimeSeriesChart;
